// Fase G (Líderes) — REQ-088. Reconstruye el árbol de jerarquía SOLO entre los líderes que trae el
// listado (`GET v1/analytics/recognition/leaders-list`), usando `managerId`. Un líder cuyo manager no
// aparece en el mismo listado (fuera de scope, o sin equipo propio) se pinta como raíz de su propia
// rama — «Nivel» ya no es el campo del DTO (que el backend nunca calcula, ver
// `LeaderAggregateRowDto.nivel`) sino la profundidad real de este árbol reconstruido.
export interface LeaderTreeEntry<T> {
  row: T;
  /** 0 = raíz. «Nivel» que se pinta en pantalla es `depth + 1` (N-1, N-2, N-3…). */
  depth: number;
  /**
   * Posición en la lista final aplanada (pre-order). Estable entre páginas: el color del avatar se
   * asigna con este índice, no con el índice de la página visible, para que un líder no cambie de
   * color al paginar.
   */
  index: number;
}

export function buildLeaderForest<T>(
  rows: readonly T[],
  getId: (row: T) => string,
  getManagerId: (row: T) => string | null | undefined,
): LeaderTreeEntry<T>[] {
  const rowsById = new Map<string, T>();
  rows.forEach((row) => rowsById.set(getId(row), row));

  const childrenByManagerId = new Map<string, T[]>();
  const roots: T[] = [];
  rows.forEach((row) => {
    const managerId = getManagerId(row);
    if (managerId && rowsById.has(managerId)) {
      const siblings = childrenByManagerId.get(managerId) ?? [];
      siblings.push(row);
      childrenByManagerId.set(managerId, siblings);
    } else {
      roots.push(row);
    }
  });

  const entries: LeaderTreeEntry<T>[] = [];
  const visitedIds = new Set<string>();

  function visit(row: T, depth: number): void {
    const rowId = getId(row);
    if (visitedIds.has(rowId)) return;
    visitedIds.add(rowId);

    entries.push({ row, depth, index: entries.length });
    const children = childrenByManagerId.get(rowId) ?? [];
    children.forEach((child) => visit(child, depth + 1));
  }

  roots.forEach((root) => visit(root, 0));

  // Defensa ante un ciclo de ManagerId corrupto en el directorio: un ciclo (A reporta a B, B reporta
  // a A) no produce ninguna raíz "natural" y ambos quedarían fuera de `entries` en silencio — un
  // líder real desaparecería de la tabla sin ningún error visible. Cualquier fila que la recursión no
  // haya alcanzado se agrega como raíz de su propia rama: nunca se pierde una fila, en el peor caso se
  // pinta con una jerarquía incompleta.
  rows.forEach((row) => {
    if (!visitedIds.has(getId(row))) visit(row, 0);
  });

  return entries;
}

// La paleta se mudó a `@/utils/avatarPalette` el 2026-09-03: es la misma de toda la app, no una de
// la tabla de Líderes. Se conservan los nombres como alias para no tocar a los consumidores.
export { AVATAR_PALETTE as LEADER_AVATAR_COLORS, avatarColorByIndex as leaderAvatarColor } from '@/utils/avatarPalette';
